/**
 * @file WmsSkuPickerDialog.tsx
 * @description Подбор фасовок (SKU) для складских документов (phase19.13 этап 0).
 *
 * Склад учитывает остаток по фасовке, поэтому в приёмке/списании выбирается не
 * товар, а конкретный артикул. Источник данных — складской пикер
 * `GET /admin/wms/skus` (право SHOP_WMS_VIEW, без цен): заказной
 * `/admin/orders/skus` кладовщику недоступен по правам.
 *
 * Warehouse-UI: строки ≥56px, поиск в фокусе (USB-сканер вводит артикул как
 * клавиатура), sticky-футер с крупными кнопками.
 */

import { Loader2, Package, Search } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { WmsSkuPickerRow } from "./WmsSkuPickerRow";
import { wmsSkusApi, type WmsSkuPickerItem } from "../../lib/api-wms";

/** Фасовка в пикере — контракт `GET /admin/wms/skus`. */
export type WmsPickerSku = WmsSkuPickerItem;

interface Props {
  open: boolean;
  onClose: () => void;
  onSelectMany: (skus: WmsPickerSku[]) => void;
  /** Уже добавленные skuId — показываем как disabled, чтобы не дублировать. */
  alreadyAddedIds?: string[];
}

export function WmsSkuPickerDialog({
  open,
  onClose,
  onSelectMany,
  alreadyAddedIds = [],
}: Props) {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<WmsPickerSku[]>([]);
  /** Всего под фильтром — показываем, когда список обрезан лимитом. */
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Map<string, WmsPickerSku>>(new Map());

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      wmsSkusApi
        .search({ search: search.trim() || undefined, limit: 100 })
        .then((page) => {
          if (cancelled) return;
          setItems(page.items);
          setTotal(page.total);
          setError(null);
        })
        .catch(() => {
          if (cancelled) return;
          setItems([]);
          setTotal(0);
          setError("Не удалось загрузить список фасовок");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, open]);

  // Сброс состояния при закрытии — иначе следующий вызов покажет старый выбор.
  useEffect(() => {
    if (!open) {
      setSelected(new Map());
      setSearch("");
    }
  }, [open]);

  function toggle(sku: WmsPickerSku) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(sku.id)) next.delete(sku.id);
      else next.set(sku.id, sku);
      return next;
    });
  }

  return (
    <Sheet open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
        <SheetHeader className="shrink-0 border-b border-on-surface/8 px-4 py-3">
          <SheetTitle>Подбор фасовок</SheetTitle>
        </SheetHeader>

        <div className="shrink-0 border-b border-on-surface/8 px-4 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-on-surface-variant" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Название товара или артикул"
              className="min-h-[56px] pl-9 text-base"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="size-6 animate-spin text-primary" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-sm text-on-surface-variant">
              <Package className="size-8" />
              {error ?? "Фасовок не найдено"}
            </div>
          ) : (
            <>
              <ul className="flex flex-col divide-y divide-on-surface/8">
                {items.map((sku) => (
                  <WmsSkuPickerRow
                    key={sku.id}
                    sku={sku}
                    checked={selected.has(sku.id)}
                    disabled={alreadyAddedIds.includes(sku.id)}
                    onToggle={() => toggle(sku)}
                  />
                ))}
              </ul>
              {total > items.length ? (
                <p className="px-4 py-3 text-center text-xs text-on-surface-variant">
                  Показаны {items.length} из {total} — уточните поиск.
                </p>
              ) : null}
            </>
          )}
        </div>

        <SheetFooter className="shrink-0 flex-row items-center justify-between gap-2 border-t border-on-surface/8 px-4 py-3">
          <span className="text-sm text-on-surface-variant">
            Выбрано: <b className="text-on-surface">{selected.size}</b>
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="min-h-[56px]">
              Отмена
            </Button>
            <Button
              onClick={() => onSelectMany([...selected.values()])}
              disabled={selected.size === 0}
              className="min-h-[56px]"
            >
              Добавить {selected.size > 0 ? `(${selected.size})` : ""}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

